import React, {ReactNode, useState} from "react"
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Divider,
	FormControlLabel,
	LinearProgress,
	TextField,
	Typography,
} from "@mui/material"

import {SMS_REPLY_FACTS, SMS_SEGMENT_LIMIT, replyBudget, segmentCount} from "./smsReplyBudget"

import {colors} from "@indebted/theme/colors"

type SMSDraft = {
	text: string
	modelVersion: string
}

type GenerateDraftRequest = {
	guidance: string
	fields: string[]
}

type SMSDraftReplyPanelProps = {
	fromNumber: string
	toNumber: string
	onGenerateDraft?: (request: GenerateDraftRequest) => Promise<SMSDraft>
	onSendReply?: (reply: {text: string; edited: boolean}) => Promise<void>
}

type SendState = {kind: "idle"} | {kind: "sent"} | {kind: "error"}

function SMSDraftReplyPanel({fromNumber, toNumber, onGenerateDraft, onSendReply}: SMSDraftReplyPanelProps) {
	const [guidance, setGuidance] = useState("")
	const [fields, setFields] = useState<string[]>([])
	const [draft, setDraft] = useState<SMSDraft | null>(null)
	const [text, setText] = useState("")
	const [generating, setGenerating] = useState(false)
	const [sending, setSending] = useState(false)
	const [generateFailed, setGenerateFailed] = useState(false)
	const [sendState, setSendState] = useState<SendState>({kind: "idle"})

	const budget = replyBudget(fields)

	function toggleField(field: string, checked: boolean) {
		setFields((current) => (checked ? [...current, field] : current.filter((f) => f !== field)))
	}

	async function generateDraft() {
		if (!onGenerateDraft) return
		setGenerating(true)
		setGenerateFailed(false)
		setSendState({kind: "idle"})
		try {
			const generated = await onGenerateDraft({guidance, fields})
			setDraft(generated)
			setText(generated.text)
		} catch {
			setGenerateFailed(true)
		} finally {
			setGenerating(false)
		}
	}

	async function sendReply() {
		if (!onSendReply || !draft) return
		setSending(true)
		try {
			await onSendReply({text, edited: text !== draft.text})
			setSendState({kind: "sent"})
		} catch {
			setSendState({kind: "error"})
		} finally {
			setSending(false)
		}
	}

	return (
		<PanelColumn>
			<Box display="flex" alignItems="center" gap={1}>
				<Typography variant="subtitle2" color="textPrimary">
					Draft a reply
				</Typography>
				<Chip label="SMS" size="small" />
			</Box>

			<Box borderRadius={1} p={1.5} sx={{bgcolor: colors.gray["100"]}}>
				<NumberRow label="From" number={fromNumber} />
				<NumberRow label="To" number={toNumber} />
				<Typography variant="caption" color="textSecondary">
					Taken from the message you are replying to. Not editable.
				</Typography>
			</Box>

			<TextField
				label="Instructions"
				placeholder="e.g. offer a 30-day hardship pause"
				value={guidance}
				onChange={(event) => setGuidance(event.target.value)}
				multiline
				minRows={4}
				fullWidth
				size="small"
			/>

			<FieldSet label={`Include these customer details · budget ${SMS_SEGMENT_LIMIT} characters`}>
				<Box display="grid" gridTemplateColumns="repeat(2, 1fr)" columnGap={2}>
					{SMS_REPLY_FACTS.map(({field, label, estimatedCharacters}) => (
						<FormControlLabel
							key={field}
							control={
								<Checkbox
									checked={fields.includes(field)}
									onChange={(event) => toggleField(field, event.target.checked)}
								/>
							}
							label={
								<Box display="flex" alignItems="baseline" gap={0.75}>
									<Typography variant="body2">{label}</Typography>
									<Typography variant="caption" color="textSecondary">
										~{estimatedCharacters}
									</Typography>
								</Box>
							}
						/>
					))}
				</Box>
				<LinearProgress
					variant="determinate"
					value={Math.min((budget.reserved / budget.limit) * 100, 100)}
					color={budget.overBudget ? "error" : "primary"}
				/>
				<Typography variant="caption" color={budget.overBudget ? "error" : "textSecondary"}>
					{budget.reserved} of {budget.limit} characters reserved for facts · {budget.remaining} left for the
					message
				</Typography>
			</FieldSet>

			<Button
				variant="contained"
				color="primary"
				disabled={!onGenerateDraft || generating}
				onClick={generateDraft}
				sx={{alignSelf: "flex-start"}}
			>
				{generating ? <CircularProgress size={24} /> : "Generate draft"}
			</Button>
			{!onGenerateDraft && (
				<Typography variant="caption" color="textSecondary">
					Drafting an SMS reply is not available yet.
				</Typography>
			)}
			{generateFailed && (
				<Alert severity="error" sx={{borderRadius: 2}}>
					{"Couldn't generate the draft. Try again."}
				</Alert>
			)}

			{draft && (
				<>
					<Divider />
					{sendState.kind === "sent" ? (
						<Alert severity="success" sx={{borderRadius: 2}}>
							Reply sent
						</Alert>
					) : (
						<DraftPreview
							text={text}
							onTextChange={setText}
							onSend={sendReply}
							canSend={Boolean(onSendReply)}
							sending={sending}
							sendFailed={sendState.kind === "error"}
						/>
					)}
				</>
			)}
		</PanelColumn>
	)
}

function DraftPreview({
	text,
	onTextChange,
	onSend,
	canSend,
	sending,
	sendFailed,
}: {
	text: string
	onTextChange: (text: string) => void
	onSend: () => void
	canSend: boolean
	sending: boolean
	sendFailed: boolean
}) {
	const segments = segmentCount(text)
	const overOneSegment = segments > 1

	return (
		<PanelColumn>
			<Typography variant="subtitle2" color="textPrimary">
				Drafted message
			</Typography>
			<TextField
				value={text}
				onChange={(event) => onTextChange(event.target.value)}
				multiline
				fullWidth
				size="small"
			/>
			<Typography variant="caption" color={overOneSegment ? "warning.main" : "textSecondary"}>
				{text.length} characters · {segments} segment{segments === 1 ? "" : "s"}
			</Typography>
			{sendFailed && (
				<Alert severity="error" sx={{borderRadius: 2}}>
					{"Couldn't send the reply. Try again."}
				</Alert>
			)}
			<Button
				variant="contained"
				color="primary"
				disabled={!canSend || sending || text.trim() === ""}
				onClick={onSend}
				sx={{alignSelf: "flex-start"}}
			>
				{sending ? <CircularProgress size={24} /> : "Approve & send"}
			</Button>
			{!canSend && (
				<Typography variant="caption" color="textSecondary">
					Sending an SMS reply is not available yet.
				</Typography>
			)}
		</PanelColumn>
	)
}

function NumberRow({label, number}: {label: string; number: string}) {
	return (
		<Box display="flex" gap={2}>
			<Typography variant="caption" color="textSecondary" width={40}>
				{label}
			</Typography>
			<Typography variant="body2" color="textPrimary" fontWeight={600}>
				{number}
			</Typography>
		</Box>
	)
}

function PanelColumn({children}: {children: ReactNode}) {
	return (
		<Box display="flex" flexDirection="column" gap={2}>
			{children}
		</Box>
	)
}

function FieldSet({label, children}: {label: string; children: ReactNode}) {
	return (
		<Box
			display="flex"
			flexDirection="column"
			gap={1}
			border="1px solid"
			borderColor="grey.200"
			borderRadius={1}
			p={1.5}
		>
			<Typography variant="caption" color="textSecondary" textTransform="uppercase" letterSpacing={0.5}>
				{label}
			</Typography>
			{children}
		</Box>
	)
}

export {SMSDraftReplyPanel}
export type {SMSDraft, GenerateDraftRequest}
